import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Sparkles, Users, Award, Building2, Globe2,
  Plane, MessageCircle, ArrowRight, CheckCircle2, Cpu, BadgeCheck,
  Briefcase, Wallet, LifeBuoy, Trophy,
} from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';

const TRUST_METRICS = [
  { label: 'Years of Experience', value: '23+', Icon: Award },
  { label: 'Airline Tickets Issued', value: '100,000+', Icon: Plane },
  { label: 'Visas Processed', value: '100,000+', Icon: BadgeCheck },
  { label: 'Team Members', value: '40+', Icon: Users },
];

const DIFFERENTIATORS = [
  {
    Icon: ShieldCheck,
    title: 'Trusted Since 2002',
    body: "Over 23 years of hands-on travel industry experience serving individual travelers and travel businesses across India. We have grown from a small team into one of the country's most trusted names in the Middle East travel segment.",
  },
  {
    Icon: Briefcase,
    title: 'Strong B2B Network',
    body: "We are recognized as one of India's largest B2B aggregators for Middle East travel. Travel agents and businesses across the country rely on our network, inventory access, and operational expertise to serve their own customers with confidence.",
  },
  {
    Icon: Plane,
    title: 'Authorized Airline Partnerships',
    body: 'As Authorized Selling Agents for Iran Air and Mahan Air, we carry long-standing relationships built on performance and trust. For over a decade, we have consistently ranked among the top-performing sellers for Iran Air in the region.',
  },
  {
    Icon: Cpu,
    title: 'Technology-Driven OTA',
    body: 'ACF Tourism has expanded beyond traditional travel operations into a full-fledged Online Travel Platform. We are building scalable booking infrastructure, intelligent travel systems, and digital-first experiences for both customers and travel partners.',
  },
  {
    Icon: LifeBuoy,
    title: 'Dedicated Operational Support',
    body: 'We understand that travel involves real people with real deadlines. Our team provides responsive, reliable operational support across ticketing, visa processing, and customer queries — available through multiple channels including WhatsApp.',
  },
  {
    Icon: Trophy,
    title: 'Industry Presence',
    body: 'ACF Tourism has consistently represented itself at major travel trade exhibitions including OTM, TTF, and ITM. Our active industry participation reflects our commitment to building long-term relationships within the global travel ecosystem.',
  },
  {
    Icon: Wallet,
    title: 'Transparent Pricing',
    body: 'We believe travel booking should be simple and honest. Every booking on our platform comes with a clear fare breakdown — base fare, airline taxes, service fee, and payment processing charges — with no hidden fees at checkout.',
  },
];

const TRUST_POINTS = [
  'Established and experienced — operating since 2002',
  'Over 100,000 tickets issued and 100,000 visas processed',
  'Authorized airline partner status with Iran Air and Mahan Air',
  'Recognized by flydubai, Jazeera Airways, SalamAir, and Oman Air',
  "One of India's largest B2B aggregators for Middle East travel",
  'Transparent checkout with no hidden charges',
  'Dedicated support available via WhatsApp and multiple channels',
  'Active presence at national and international travel trade exhibitions',
  'Modern OTA platform with intelligent booking technology',
];

const SEGMENTS = [
  { title: 'Air Ticketing', body: 'Domestic and international flight bookings across multiple airlines and routes.' },
  { title: 'Visa Processing', body: 'End-to-end visa assistance for key Middle East destinations.' },
  { title: 'B2B Travel Solutions', body: 'Inventory access, support, and partner management for travel agents nationwide.' },
  { title: 'B2C Travel Services', body: 'Direct booking experiences for individual travelers and families.' },
  { title: 'Charter Operations', body: 'Expertise in group charter flights for specialized travel segments.' },
  { title: 'Digital Travel Technology', body: 'OTA platform and automated travel workflows.' },
];

const PLATFORM_HIGHLIGHTS = [
  'Real-time flight search and booking',
  'Automated ticket issuance and delivery',
  'Digital visa processing workflows',
  'Scalable B2B partner portal',
];

const AIRLINES = [
  'Iran Air', 'Mahan Air', 'flydubai', 'Jazeera Airways', 'SalamAir', 'Oman Air',
];

const WhyAcfTourism = () => (
  <LegalLayout
    title="Why Choose ACF Tourism"
    subtitle="23+ years of travel expertise, operational reliability, and modern travel technology — all in one platform."
    breadcrumbLabel="Company"
    canonicalPath="/why-acf-tourism"
    description="Why travelers and agents trust ACF Tourism: 23+ years experience, 100,000+ tickets, authorized Iran Air & Mahan Air partner, transparent pricing, modern OTA platform."
    testId="why-acf-tourism-page"
  >
    {/* Trust metrics with icons */}
    <div className="not-prose grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
      {TRUST_METRICS.map(({ label, value, Icon }) => (
        <div
          key={label}
          className="text-center p-5 bg-gradient-to-br from-[#F5FBFB] to-white border border-[#659A9B]/20 rounded-xl"
          data-testid={`why-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Icon className="w-6 h-6 mx-auto mb-2 text-[#659A9B]" />
          <div className="text-2xl md:text-3xl font-bold text-[#1B375B]">{value}</div>
          <div className="text-xs text-gray-600 mt-1">{label}</div>
        </div>
      ))}
    </div>

    <h2>What Makes ACF Different</h2>
    <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
      {DIFFERENTIATORS.map(({ Icon, title, body }) => (
        <div
          key={title}
          className="p-5 bg-white border border-[#659A9B]/20 rounded-xl hover:border-[#659A9B]/50 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-[#1B375B]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#1B375B] mb-1.5 mt-0">{title}</h3>
              <p className="text-sm text-gray-600 m-0 leading-relaxed">{body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>

    <h2>Trusted and Recognized by Leading International Airlines</h2>
    <p>
      Our operational reliability and commitment to service have earned appreciation and
      recognition from leading international carriers. We work closely with Iran Air,
      Mahan Air, flydubai, Jazeera Airways, SalamAir, and Oman Air — and continue to
      strengthen our airline partnerships across key international routes.
    </p>
    <div className="not-prose flex flex-wrap gap-2 my-6">
      {AIRLINES.map((a) => (
        <span
          key={a}
          className="px-3 py-1.5 bg-[#1B375B]/5 border border-[#1B375B]/15 rounded-full text-sm font-medium text-[#1B375B]"
        >
          <Plane className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />{a}
        </span>
      ))}
    </div>

    <h2>Modern Travel Technology, Built for the Future</h2>
    <p>
      ACF Tourism is actively investing in digital travel infrastructure to deliver faster,
      smarter, and more seamless booking experiences. Our OTA platform combines real-time
      inventory access, intelligent booking flows, and scalable systems designed for both
      B2C travelers and B2B travel partners.
    </p>
    <p>
      We are building towards a future where travel booking is not just convenient — but
      genuinely intelligent. From automated ticketing workflows to AI-assisted travel support,
      ACF Tourism is evolving into a full-stack travel technology company.
    </p>
    <h3>Platform highlights</h3>
    <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-2.5 my-5">
      {PLATFORM_HIGHLIGHTS.map((h) => (
        <div key={h} className="flex items-center gap-2.5 p-3 bg-[#F5FBFB] rounded-lg">
          <Sparkles className="w-4 h-4 text-[#659A9B] flex-shrink-0" />
          <span className="text-sm text-gray-800">{h}</span>
        </div>
      ))}
    </div>

    <h2>Why Customers Trust ACF</h2>
    <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-2.5 my-6">
      {TRUST_POINTS.map((t) => (
        <div key={t} className="flex items-start gap-2.5">
          <CheckCircle2 className="w-4 h-4 text-[#659A9B] flex-shrink-0 mt-0.5" />
          <span className="text-sm text-gray-700">{t}</span>
        </div>
      ))}
    </div>

    <h2>End-to-End Travel Expertise</h2>
    <p>
      ACF Tourism has built deep operational expertise across every major segment of the
      travel industry. Whether it is air ticketing for individual travelers, bulk inventory
      management for B2B partners, visa processing at scale, or complete international travel
      solutions — our team has the knowledge, the network, and the systems to deliver.
    </p>
    <div className="not-prose grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 my-6">
      {SEGMENTS.map((s) => (
        <div key={s.title} className="p-4 bg-white border border-[#659A9B]/20 rounded-xl">
          <div className="flex items-center gap-2 mb-1.5">
            <Globe2 className="w-4 h-4 text-[#659A9B]" />
            <span className="font-semibold text-[#1B375B] text-sm">{s.title}</span>
          </div>
          <p className="text-xs text-gray-600 m-0 leading-relaxed">{s.body}</p>
        </div>
      ))}
    </div>

    <h2>Building the Future of Travel</h2>
    <p>
      The travel industry is changing rapidly. Travelers expect faster decisions, smarter
      recommendations, and seamless digital experiences. ACF Tourism is committed to being
      at the forefront of that change.
    </p>
    <p>
      We are transforming from a traditional travel operations company into a scalable
      travel technology platform — one that combines over 23 years of industry depth with
      the capabilities of modern digital infrastructure.
    </p>
    <p>
      Our vision is simple: to make travel booking more intelligent, more transparent, and
      more reliable for every traveler and every travel partner we serve.
    </p>

    {/* Final CTA */}
    <div className="not-prose mt-10 p-6 md:p-8 bg-gradient-to-br from-[#1B375B] to-[#335E77] text-white rounded-2xl">
      <h3 className="text-xl md:text-2xl font-bold mb-2">Ready to Experience the ACF Difference?</h3>
      <p className="text-white/85 text-sm md:text-base leading-relaxed mb-5">
        Whether you are booking a flight, processing a visa, or looking for a trusted travel
        partner for your business — ACF Tourism has the experience, the technology, and the
        team to deliver.
      </p>
      <div className="flex flex-wrap gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#1B375B] font-semibold rounded-lg hover:bg-[#E7EEEE] transition-colors"
          data-testid="why-cta-search"
        >
          <Plane className="w-4 h-4" />
          Search Flights
        </Link>
        <Link
          to="/contact-us"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/25 transition-colors"
          data-testid="why-cta-contact"
        >
          <MessageCircle className="w-4 h-4" />
          Contact Our Team
        </Link>
        <Link
          to="/about-us"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/25 transition-colors"
          data-testid="why-cta-about"
        >
          About Us
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </LegalLayout>
);

export default WhyAcfTourism;
